// "Help me understand this" under the integrity readback: starts the
// explanation, polls for it, and reveals it — flipping `hidden` on blocks the
// server already rendered (mock_reports/_integrity_explanation.html.erb).
//
// question_elaboration.js's shape, trimmed: one card per page, no toggle, no
// maths. The offer is a real form post; this module intercepts the submit so
// the page does not reload, and the page still works with the pack absent.
//
// The give-up clock is the server's own STALE_AFTER, shipped in a data
// attribute, and every path that keeps polling goes through `schedule`, so
// no failure mode polls for ever with "Try again" never revealed.

const FIRST_POLL_MS = 1500;
const MAX_POLL_MS = 5000;
const GRACE_MS = 30000;

function csrfToken() {
  const meta = document.querySelector("meta[name=csrf-token]");
  return meta ? meta.content : "";
}

function setupCard(root) {
  const url = root.dataset.url;
  const staleAfterMs = Number(root.dataset.staleAfterMs) || 300000;

  const blocks = {
    offer: root.querySelector("[data-explain-offer]"),
    working: root.querySelector("[data-explain-working]"),
    failed: root.querySelector("[data-explain-failed]"),
    ready: root.querySelector("[data-explain-ready]"),
  };
  const body = root.querySelector("[data-explain-body]");
  const error = root.querySelector("[data-explain-error]");
  const failedMessage = root.querySelector("[data-explain-failed-message]");
  const topup = root.querySelector("[data-explain-topup]");

  let startedAt = 0;
  let timer = null;

  function show(state) {
    Object.entries(blocks).forEach(([name, el]) => {
      if (el) el.hidden = name !== state;
    });
    root.dataset.state = state;
  }

  function fail(message) {
    if (failedMessage && message) failedMessage.textContent = message;
    show("failed");
  }

  // `html` is the server's own render of _integrity_explanation_body, from a
  // same-origin JSON reply — the same trust as the page itself.
  function land(html) {
    if (body) body.innerHTML = html;
    show("ready");
  }

  function apply(data) {
    if (data.readable && data.html) return land(data.html);
    if (data.restartable) return fail(data.error);
    if (data.working) return schedule();
    return fail(data.error);
  }

  function poll() {
    fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => (data === null ? schedule() : apply(data)))
      .catch(schedule);
  }

  function schedule() {
    const elapsed = Date.now() - startedAt;
    if (elapsed > staleAfterMs + GRACE_MS) {
      return fail("That one is taking longer than it should. Try again — you have not been charged for it.");
    }
    const wait = Math.min(MAX_POLL_MS, FIRST_POLL_MS + elapsed / 10);
    timer = window.setTimeout(poll, wait);
  }

  // Back to the offer, with the reason under the button. Nothing was spent.
  function refuse(message) {
    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
    show("offer");
  }

  function start(event) {
    event.preventDefault();
    if (error) error.hidden = true;
    window.clearTimeout(timer);
    startedAt = Date.now();
    show("working");

    fetch(url, {
      method: "POST",
      headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
      credentials: "same-origin",
    })
      .then((response) => response.json().then((data) => ({ status: response.status, data })))
      .then(({ status, data }) => {
        if (status === 402) {
          if (topup) topup.hidden = false;
          return refuse(data.message || "You do not have enough tokens for this yet.");
        }
        if (status === 429) return refuse(data.message || "You have asked for a lot of these today. Try again later.");
        if (status >= 400) return refuse("We could not start this one. Try again in a moment.");
        return apply(data);
      })
      .catch(() => refuse("We could not reach the server. Check your connection and try again."));
  }

  root.querySelectorAll("[data-explain-form]").forEach((form) => {
    form.addEventListener("submit", start);
  });

  // Arrived while a generation was in flight: join the wait.
  if (root.dataset.state === "working") {
    startedAt = Date.now();
    schedule();
  }
}

function setup() {
  document.querySelectorAll("[data-integrity-explanation]").forEach(setupCard);
}

// The same guard every module in this pack carries — see mock_request_wizard.js.
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", setup);
} else {
  setup();
}
